import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { REHYDRATE } from 'redux-persist';
import { RootState } from './types';

/**
 * 'default' is kept in the union only for back-compat with persisted state
 * written by older builds. It is normalized to 'name_asc' on REHYDRATE and
 * must not be surfaced in UI. New code paths should treat 'name_asc' as the
 * canonical default (A–Z, client-sorted).
 */
export type ProductSortOption =
  | 'default'
  | 'name_asc'
  | 'in_stock'
  | 'recent'
  | 'price_desc';

interface ProductSortState {
  sort: ProductSortOption;
}

const initialState: ProductSortState = {
  sort: 'name_asc',
};

const slice = createSlice({
  name: 'productSort',
  initialState,
  reducers: {
    setProductSort: (state, action: PayloadAction<ProductSortOption>) => {
      state.sort = action.payload === 'default' ? 'name_asc' : action.payload;
    },
    resetProductSort: state => {
      state.sort = 'name_asc';
    },
  },
  extraReducers: builder => {
    // Migrate persisted 'default' → 'name_asc' on rehydrate so existing
    // installs pick up the new default without the user having to toggle it.
    builder.addCase(REHYDRATE, state => {
      if (state.sort === 'default') state.sort = 'name_asc';
      if ((state.sort as string) === 'most_selling') state.sort = 'in_stock';
    });
  },
});

export const { setProductSort, resetProductSort } = slice.actions;
export default slice.reducer;

export const selectProductSort = (state: RootState) =>
  (state as any).productSort.sort;

// Helper for backend query mapping (attach to your endpoint)
export const productSortQueryValue: Record<ProductSortOption, string> = {
  default: 'name_asc',
  name_asc: 'name_asc',
  in_stock: 'in_stock',
  recent: 'recent',
  price_desc: 'price_desc',
};
